import React from 'react';
import { Box, Link, Typography, TypographyProps } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { GREENING_CONTACT_EMAIL } from './constants';
import { GreeningBrandTermVariant } from './greeningBrandTerms';
import { GREENING_BRAND } from './greeningFabActions';
import { parseGreeningBrandText, splitByEmail } from './greeningBrandTextUtils';

interface Props {
  text: string;
  variant?: TypographyProps['variant'];
  component?: React.ElementType;
  sx?: TypographyProps['sx'];
  /** Destaca mailto para contato@greenplat.com (respostas rate_limited). */
  linkifyEmail?: boolean;
}

function brandSx(variant: GreeningBrandTermVariant, theme: Theme) {
  switch (variant) {
    case 'oaas':
      return { color: theme.palette.success.dark, fontWeight: 600 };
    case 'greenplat':
      return { color: theme.palette.success.main, fontWeight: 600 };
    case 'verde':
      return { color: GREENING_BRAND.green, fontWeight: 600 };
    case 'azul':
      return { color: GREENING_BRAND.teal, fontWeight: 600 };
    case 'ctre':
      return { color: GREENING_BRAND.orange, fontWeight: 600 };
    default:
      return { fontWeight: 600 };
  }
}

function renderSegments(text: string, theme: Theme): React.ReactNode[] {
  return parseGreeningBrandText(text).map((seg, i) => {
    if (seg.kind === 'brand') {
      return (
        <Box key={`b-${i}`} component="span" sx={brandSx(seg.variant, theme)}>
          {seg.value}
        </Box>
      );
    }
    if (seg.kind === 'strong') {
      return (
        <Box key={`s-${i}`} component="strong" sx={{ fontWeight: 700 }}>
          {seg.value}
        </Box>
      );
    }
    return <React.Fragment key={`t-${i}`}>{seg.value}</React.Fragment>;
  });
}

/** Destaca termos GreenPlat no corpo do texto — mesmo padrão da assinatura AI Greening. */
export function GreeningBrandText({
  text,
  variant = 'body2',
  component = 'div',
  sx,
  linkifyEmail = false,
}: Props) {
  const theme = useTheme();

  if (!linkifyEmail || !text.includes(GREENING_CONTACT_EMAIL)) {
    return (
      <Typography component={component} variant={variant} sx={{ whiteSpace: 'pre-wrap', ...sx }}>
        {renderSegments(text, theme)}
      </Typography>
    );
  }

  const parts = splitByEmail(text, GREENING_CONTACT_EMAIL);
  return (
    <Typography component={component} variant={variant} sx={{ whiteSpace: 'pre-wrap', ...sx }}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {renderSegments(part, theme)}
          {i < parts.length - 1 && (
            <Link href={`mailto:${GREENING_CONTACT_EMAIL}`} sx={{ fontWeight: 600 }}>
              {GREENING_CONTACT_EMAIL}
            </Link>
          )}
        </React.Fragment>
      ))}
    </Typography>
  );
}

export default GreeningBrandText;
